/* ═══════════════════════════════════════════════════════════════
   ModuleService — the firm-wide MODULE library (docs/MASTERS_UNIFIED_PLAN.md
   Phase 4, migration 047).

   ⛔ A MODULE is a reusable block-group, single-spec only — never a variant
   (that word means "a client version of a master").

   Same cloud shape every entity in this app uses: cloudCache over the generic
   EC2 table API, so reads are cached, writes invalidate, and the sync event
   tells the UI to refresh (subscribe with `useTableSync(['module_library'],
   …)`). No bespoke endpoint exists — every action is enforced server-side
   through TABLE_PERMS.module_library → the `modules` permission category.

   Archive is a SOFT subtraction (`archivedAt`) and restore is the same
   toggle; delete is permanent and gated separately (modules.delete).
   ═══════════════════════════════════════════════════════════════ */

import type { ModuleChunk, ModuleTag, ModuleVersion, SpecModule } from '../../shared/spec-modules';
import { normalizeTags, toModuleTagsArray } from '../../shared/module-categories';
import * as cache from './cloudCache';
import type { TableOpts } from './cloudCache';
import * as cloudApi from './apiClient';
import { getCurrentFirmId } from './firmContext';
import { log as logActivity } from './activityLogger';
import {
  applyMergeToCatalog,
  applyRenameToCatalog,
  tagsMissingFromCatalog,
  type CatalogOps,
} from '../../shared/module-tags';
import { deleteTagIn, mergeTagIn, renameTagIn, type TagRewrite } from '../lib/moduleTagFilter';

const TABLE = 'module_library';
const VERSIONS_TABLE = 'module_versions';
// Migration 060 — the firm's tag catalog (a tag creatable before any module uses it).
const TAGS_TABLE = 'module_tags';

// `chunks` is jsonb on the server and a parsed ModuleChunk[] in the cache —
// exactly how spec masters carry `coreContent` / `layers`.
const moduleOpts: TableOpts = { jsonFields: ['chunks', 'draftChunks'] };
const versionOpts: TableOpts = { jsonFields: ['chunks'] };

/** Modules whose draft this session already flipped to Pending publish. */
const draftKnown = new Set<string>();

/** Sync D6: the Modules list status. The server's derived `draftPending`
 *  wins; a row from an older server falls back to the raw draft. */
export function isDraftPending(m: Pick<SpecModule, 'draftPending' | 'draftChunks'>): boolean {
  return typeof m.draftPending === 'boolean' ? m.draftPending : !!m.draftChunks;
}

const byName = (a: SpecModule, b: SpecModule) =>
  (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });

export interface TagBulkResult {
  /** Modules whose tags were rewritten. */
  updated: number;
  /** Modules whose write failed (the rest still went through). */
  failed: { id: string; name: string; error: string }[];
}

/** Apply one tag rewrite to every module carrying the tag — one
 *  `updateMeta` per module, `allSettled` so one failure never strands the
 *  rest. Tags live only on modules, so this IS the whole operation. */
async function rewriteTagAcrossModules(
  rewrite: (tags: unknown) => TagRewrite,
  userName?: string
): Promise<TagBulkResult> {
  const all = await ModuleService.listAll();
  const targets = all.map((m) => ({ m, r: rewrite(m.tags) })).filter((x) => x.r.changed);
  const settled = await Promise.allSettled(
    targets.map((x) => ModuleService.updateMeta(x.m.id, { tags: x.r.tags }, userName))
  );
  const failed: TagBulkResult['failed'] = [];
  settled.forEach((s, i) => {
    if (s.status === 'rejected') {
      failed.push({
        id: targets[i].m.id,
        name: targets[i].m.name,
        error: s.reason instanceof Error ? s.reason.message : String(s.reason),
      });
    }
  });
  return { updated: targets.length - failed.length, failed };
}

const tagKey = (s: string) => s.trim().toLowerCase();
const tagFail = (what: string, err: unknown): Error =>
  new Error(`${what}: ${err instanceof Error ? err.message : String(err)}`);

/** Run the catalog row operations a rename / merge / delete implies. Sequential
 *  and awaited; the first failure throws with context. */
async function applyCatalogOps(ops: CatalogOps, existing: ModuleTag[], userName?: string) {
  const firmId = getCurrentFirmId();
  const now = new Date().toISOString();
  try {
    for (const row of ops.upsert) {
      if (existing.some((t) => t.id === row.id)) {
        await cloudApi.dbUpdate(
          TAGS_TABLE,
          { name: row.name, modified_by: userName || null, last_modified: now },
          [
            { col: 'firm_id', op: '=', val: firmId },
            { col: 'id', op: '=', val: row.id },
          ]
        );
      } else {
        await cache.save<ModuleTag>(TAGS_TABLE, row);
      }
    }
    for (const id of ops.remove) await cache.remove(TAGS_TABLE, id);
  } catch (err) {
    throw tagFail('The tag catalog could not be updated', err);
  } finally {
    cache.invalidateAndNotify(TAGS_TABLE);
  }
}

export const ModuleService = {
  /** Every module the firm has, archived ones included — the library page
   *  shows both and filters in the UI, so one fetch serves both views. */
  listAll: async (): Promise<SpecModule[]> => {
    const rows = await cache.list<SpecModule>(TABLE, moduleOpts);
    // Every module row enters the renderer right here — one normalization
    // boundary for `tags` (see `toModuleTagsArray`) instead of trusting the
    // cache, the optimistic write, and every broadcast to agree on shape.
    return [...rows].sort(byName).map((m) => ({ ...m, tags: toModuleTagsArray(m.tags) }));
  },

  /** Live modules only — what an insert (Phase 5) may ever offer. */
  list: async (): Promise<SpecModule[]> =>
    (await ModuleService.listAll()).filter((m) => !m.archivedAt),

  get: async (id: string): Promise<SpecModule | null> =>
    (await ModuleService.listAll()).find((m) => m.id === id) || null,

  /**
   * CREATE a definition (Save as module…). Callers pass a definition built by
   * `buildModuleDefinition` (shared/spec-modules.ts) — the one brain that
   * knows how to chunk and anchor.
   *
   * A whole-record save is the server's CREATE path for this table
   * (`modules.create`); editing an existing row goes through `rename` below,
   * which the server reads as `modules.edit`. Keeping the two apart is what
   * makes the catalog's create/edit split mean something.
   */
  create: async (module: SpecModule): Promise<SpecModule> => {
    const saved = await cache.save<SpecModule>(TABLE, module, moduleOpts);
    // The server mints v1's version row as a side effect of this save
    // (writeModuleVersionSnapshot) — the client never writes module_versions
    // directly, but its cache doesn't know that happened, so drop the cache
    // rather than let the Versions panel show stale (empty) history.
    cache.invalidate(VERSIONS_TABLE);
    logActivity({
      action: 'module.create',
      entityType: 'module',
      entityId: saved.id,
      metadata: { name: saved.name, chunks: (saved.chunks || []).length },
    });
    return saved;
  },

  /** Rename / re-describe. Metadata only — the captured blocks are untouched,
   *  so the version does NOT move (a version bump means the DEFINITION
   *  changed, which is what Phase 6 will PR out). Sent as a partial update:
   *  the server gates that on `modules.edit`. */
  rename: async (
    id: string,
    name: string,
    description: string | undefined,
    userName?: string
  ): Promise<void> => {
    const firmId = getCurrentFirmId();
    await cloudApi.dbUpdate(
      TABLE,
      {
        name: name.trim(),
        description: description?.trim() || null,
        modified_by: userName || null,
        last_modified: new Date().toISOString(),
      },
      [
        { col: 'firm_id', op: '=', val: firmId },
        { col: 'id', op: '=', val: id },
      ]
    );
    cache.invalidate(TABLE);
    logActivity({
      action: 'module.update',
      entityType: 'module',
      entityId: id,
      metadata: { name: name.trim() },
    });
  },

  /**
   * Archive or restore — the same toggle, as the permission catalog says.
   * Sent as a THREE-key partial update on purpose: the server reads an
   * `archived_at` flip of three keys or fewer as an archive toggle and gates
   * it on `modules.archive` (ec2-api/server.js). A whole-record save would
   * demand `modules.edit` instead and lock archiving away from the people
   * who are meant to have it.
   */
  setArchived: async (id: string, archived: boolean, userName?: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    await cloudApi.dbUpdate(
      TABLE,
      {
        archived_at: archived ? new Date().toISOString() : null,
        modified_by: userName || null,
        last_modified: new Date().toISOString(),
      },
      [
        { col: 'firm_id', op: '=', val: firmId },
        { col: 'id', op: '=', val: id },
      ]
    );
    cache.invalidate(TABLE);
    logActivity({
      action: archived ? 'module.archive' : 'module.restore',
      entityType: 'module',
      entityId: id,
    });
  },

  /**
   * A fresh read of one module (cache dropped first) — the builder needs the
   * server's CURRENT `draftChunks` / `chunks` / `version` when it opens a
   * shared draft, never a cached copy.
   */
  getFresh: async (id: string): Promise<SpecModule | null> => {
    cache.invalidate(TABLE);
    return ModuleService.get(id);
  },

  /**
   * The editor's JSON mirror of the shared draft (Sync D14): the server-held
   * `draft_chunks`. Sent as a generic partial update with the `collabMirror`
   * wire flag — gated `modules.edit`, never bumps the version, never touches
   * the published `chunks`. `chunks` is pre-encoded (jsonb column).
   */
  writeDraftMirror: async (id: string, chunks: ModuleChunk[]): Promise<void> => {
    await cloudApi.dbUpdate(TABLE, { draft_chunks: JSON.stringify(chunks), collabMirror: true }, [
      { col: 'firm_id', op: '=', val: getCurrentFirmId() },
      { col: 'id', op: '=', val: id },
    ]);
    // The author's own lists never hear their own broadcast: the first mirror
    // write of a draft flips the row to Pending publish here (once per draft).
    if (!draftKnown.has(id)) {
      draftKnown.add(id);
      cache.invalidateAndNotify(TABLE);
    }
  },

  /**
   * Discard the shared draft for everyone. NO mirror flag on purpose: the
   * server reads a plain `draft_chunks: null` as a reset, clears the live
   * document and closes every peer's socket (4410).
   */
  discardDraft: async (id: string): Promise<void> => {
    draftKnown.delete(id);
    await cloudApi.dbUpdate(TABLE, { draft_chunks: null }, [
      { col: 'firm_id', op: '=', val: getCurrentFirmId() },
      { col: 'id', op: '=', val: id },
    ]);
    cache.invalidateAndNotify(TABLE);
    logActivity({ action: 'module.discard_draft', entityType: 'module', entityId: id });
  },

  /**
   * PUBLISH the draft (Sync D14). The server (gated `modules.publish`) takes
   * its own `draft_chunks` as the new definition, bumps the version, writes
   * the version row, clears the draft, evicts the live document and raises
   * the update reviews at masters + project specs — the client no longer
   * raises anything itself.
   */
  publish: async (
    id: string,
    message?: string
  ): Promise<{ version: number | null; raised: number }> => {
    draftKnown.delete(id);
    const res = (await cloudApi.modulePublish({
      moduleId: id,
      ...(message?.trim() ? { message: message.trim() } : {}),
    })) as Record<string, unknown> | null;
    cache.invalidate(TABLE);
    cache.invalidate(VERSIONS_TABLE);
    cache.invalidate('spec_variant_changes');
    const version = typeof res?.version === 'number' ? res.version : null;
    logActivity({
      action: 'module.publish',
      entityType: 'module',
      entityId: id,
      metadata: { version },
    });
    return { version, raised: typeof res?.raised === 'number' ? res.raised : 0 };
  },

  /**
   * Migration 056 — set/clear this module's CATEGORY and/or TAGS. Pass only
   * the key(s) the user actually changed: an absent key is never sent, so a
   * module that never had tags never round-trips an empty `tags: []`, and a
   * server that hasn't taken migration 056 yet (the desktop client and the
   * EC2 box don't always update in lockstep) is never handed a column it
   * doesn't have unless the caller is actually trying to use it.
   *
   * ⛔ METADATA ONLY — deliberately sent as a partial update that NEVER
   * includes `chunks` or `version`. The server's update branch only mints a
   * version snapshot when `chunks` is present (ec2-api/module-categories.js
   * `shouldSnapshotModuleUpdate`); omitting both here is what keeps a
   * category/tag change from bumping the version or raising a Phase 6
   * update offer to every spec using the module — it is NOT a module
   * update as far as that feed is concerned.
   */
  updateMeta: async (
    id: string,
    patch: { categoryId?: string | null; tags?: string[] },
    userName?: string
  ): Promise<void> => {
    const firmId = getCurrentFirmId();
    const updates: Record<string, unknown> = {
      modified_by: userName || null,
      last_modified: new Date().toISOString(),
    };
    if ('categoryId' in patch) updates.category_id = patch.categoryId ?? null;
    if ('tags' in patch) updates.tags = JSON.stringify(normalizeTags(patch.tags));
    await cloudApi.dbUpdate(TABLE, updates, [
      { col: 'firm_id', op: '=', val: firmId },
      { col: 'id', op: '=', val: id },
    ]);
    cache.invalidateAndNotify(TABLE);
    logActivity({
      action: 'module.update_meta',
      entityType: 'module',
      entityId: id,
      metadata: patch,
    });
  },

  /** The firm's tag catalog (migration 060), name order. */
  listTags: async (): Promise<ModuleTag[]> => {
    const rows = await cache.list<ModuleTag>(TAGS_TABLE);
    return [...rows].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
  },

  /** Add a tag to the catalog (modules.edit). Idempotent: a case-insensitive
   *  match returns the existing row. */
  createTag: async (name: string, userName?: string): Promise<ModuleTag> => {
    const trimmed = name.trim();
    const match = (await ModuleService.listTags()).find((t) => tagKey(t.name) === tagKey(trimmed));
    if (match) return match;
    try {
      const saved = await cache.save<ModuleTag>(TAGS_TABLE, {
        id: crypto.randomUUID(),
        name: trimmed,
        createdBy: userName,
      } as ModuleTag);
      cache.invalidateAndNotify(TAGS_TABLE);
      logActivity({
        action: 'module_tag.create',
        entityType: 'module_tag',
        entityId: saved.id,
        metadata: { name: trimmed },
      });
      return saved;
    } catch (err) {
      throw tagFail(`The tag "${trimmed}" could not be created`, err);
    }
  },

  /** Make sure every tag has a catalog row (best effort per tag; allSettled). */
  ensureTagsInCatalog: async (tags: string[], userName?: string): Promise<void> => {
    const missing = tagsMissingFromCatalog(tags, await ModuleService.listTags());
    const settled = await Promise.allSettled(
      missing.map((t) => ModuleService.createTag(t, userName))
    );
    const failed = settled.filter((s) => s.status === 'rejected');
    if (failed.length) {
      console.error('[ModuleService] some tags could not be added to the catalog', failed);
    }
  },

  /** Rename a tag on every module that carries it, and in the catalog (modules.edit). */
  renameTag: async (from: string, to: string, userName?: string): Promise<TagBulkResult> => {
    const result = await rewriteTagAcrossModules((t) => renameTagIn(t, from, to), userName);
    const catalog = await ModuleService.listTags();
    await applyCatalogOps(applyRenameToCatalog(catalog, from, to), catalog, userName);
    await ModuleService.ensureTagsInCatalog([to], userName);
    return result;
  },

  /** Merge tag `from` into `into` on every module (modules.edit); a module
   *  that already has `into` just loses `from`. */
  mergeTag: async (from: string, into: string, userName?: string): Promise<TagBulkResult> => {
    const result = await rewriteTagAcrossModules((t) => mergeTagIn(t, from, into), userName);
    const catalog = await ModuleService.listTags();
    const ops = applyMergeToCatalog(catalog, from, into, (name) => ({
      id: crypto.randomUUID(),
      name,
    }));
    await applyCatalogOps(ops, catalog, userName);
    return result;
  },

  /** Remove a tag from every module that carries it (modules.edit). Every
   *  other tag on those modules is kept. */
  deleteTag: async (tag: string, userName?: string): Promise<TagBulkResult> => {
    const result = await rewriteTagAcrossModules((t) => deleteTagIn(t, tag), userName);
    const catalog = await ModuleService.listTags();
    const row = catalog.find((t) => tagKey(t.name) === tagKey(tag));
    await applyCatalogOps({ upsert: [], remove: row ? [row.id] : [] }, catalog, userName);
    return result;
  },

  /** PERMANENT delete (modules.delete). Archive is the reversible option. */
  remove: async (id: string): Promise<void> => {
    await cache.remove(TABLE, id);
    logActivity({ action: 'module.delete', entityType: 'module', entityId: id });
  },

  /**
   * Version history (Cohesion R1 Job B1). Written server-side on every
   * create/definition-update (ec2-api/server.js writeModuleVersionSnapshot)
   * — this only reads it back, newest first. Same table-generic read path
   * as `listAll`, gated identically (`modules.view`, TABLE_PERMS.
   * module_versions -> the `modules` category).
   */
  getVersions: async (moduleId: string): Promise<ModuleVersion[]> => {
    const rows = await cache.query<ModuleVersion>(VERSIONS_TABLE, { moduleId }, versionOpts);
    return [...rows].sort((a, b) => (b.version || 0) - (a.version || 0));
  },
};
